import { useQuery } from '@tanstack/react-query';
import type { MyPolicy } from '@cyphex/types/policies';
import { apiGet } from '@/lib/api/client';

/**
 * The caller's own effective admin policy.
 *
 * WHAT THIS IS FOR: not offering a button the API is going to refuse. When an
 * administrator turns "Edit sent messages" off, the honest UI does not show an
 * Edit item that answers 403 — it shows no Edit item.
 *
 * WHAT THIS IS NOT: the control. Every field is enforced in the API
 * (apps/api/src/services/policyGate.ts). If this query is loading, has failed,
 * or is served stale, the worst outcome is a button that turns out to be
 * refused — never a permission that is silently granted, which is why every
 * accessor below fails OPEN.
 *
 * `staleTime` is generous on purpose: a policy changes when an administrator
 * saves one, which is rare, and the API refuses regardless.
 */
const POLICY_STALE_MS = 5 * 60_000;

export const MY_POLICY_KEY = ['me', 'policy'] as const;

export function useMyPolicy() {
  return useQuery<MyPolicy>({
    queryKey: MY_POLICY_KEY,
    queryFn: ({ signal }) => apiGet<MyPolicy>('/me/policy', signal),
    staleTime: POLICY_STALE_MS,
    gcTime: POLICY_STALE_MS * 2,
  });
}

/** One messaging knob, defaulting to permitted while the query is in flight. */
export function useMessagingPolicy(): {
  allowEditMessages: boolean;
  allowDeleteOwnMessages: boolean;
  allowFileAttachments: boolean;
  allowVoiceMessages: boolean;
  allowScheduledSend: boolean;
  allowUrlPreviews: boolean;
} {
  const { data } = useMyPolicy();
  return {
    allowEditMessages: data?.messaging.allowEditMessages !== false,
    allowDeleteOwnMessages: data?.messaging.allowDeleteOwnMessages !== false,
    allowFileAttachments: data?.messaging.allowFileAttachments !== false,
    allowVoiceMessages: data?.messaging.allowVoiceMessages !== false,
    allowScheduledSend: data?.messaging.allowScheduledSend !== false,
    allowUrlPreviews: data?.messaging.allowUrlPreviews !== false,
  };
}
